import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { swapNarrationDialogue } from '../../../../features/scenarios/api';
import type {
  NarrationDialogueSwapConfirmation,
  NarrationDialogueSwapDryRun,
  NarrationDialogueSwapOptions,
} from '../../../../features/scenarios/types';
import { isSwapOptionsComplete, normalizeSwapOptions, optionsEqual, swapOptionsKey } from '../narrationDialogueSwap';

export const NARRATION_DIALOGUE_SWAP_DEBOUNCE_MS = 300;

export type NarrationDialogueSwapPreviewStatus = 'idle' | 'pending' | 'loading' | 'ready' | 'error';

export interface NarrationDialogueSwapConfirmedPreview {
  options: NarrationDialogueSwapOptions;
  response: NarrationDialogueSwapDryRun;
}

export interface UseNarrationDialogueSwapPreviewResult {
  /** 現在の選択条件で確認済みのdry_run応答 (条件が変わった時点でnull)。下段の表示はこれだけから描く */
  preview: NarrationDialogueSwapDryRun | null;
  status: NarrationDialogueSwapPreviewStatus;
  error: unknown;
  canExecute: boolean;
  /** 実行に送る値 (画面のstateではなく確認済みプレビューのoptionsとconfirmation)。実行できないときはnull */
  executePayload: { options: NarrationDialogueSwapOptions; confirmation: NarrationDialogueSwapConfirmation } | null;
  /** 今の選択条件でdry_runをすぐにやり直す (409 CONFIRMATION_STALEの後など) */
  refresh: () => void;
}

/**
 * 「セリフとナレーションを入れ替える」のプレビューの状態機械
 * (docs/features/scenario-narration-dialogue-swap-plan.md §3.9手順5)。
 *
 * 選択条件 (options) が変わった時点で確認済みプレビューを破棄し、300msのデバウンス後にその条件でdry_runを発行する。
 * 各リクエストに連番を付け、応答は「連番が最新」かつ「応答に使った条件が現在の条件と等しい」ときだけ採用する
 * (順序の逆転・古い条件の応答は捨てる)。実行できるのは、確認済みプレビューの条件が今の条件と一致し、
 * デバウンス待ちでも通信中でもなく、applicableで、かつ必要なら話者が指定されているときだけ。
 */
export function useNarrationDialogueSwapPreview({
  scenarioId,
  open,
  options,
}: {
  scenarioId: string | null | undefined;
  open: boolean;
  options: NarrationDialogueSwapOptions;
}): UseNarrationDialogueSwapPreviewResult {
  const key = swapOptionsKey(options);
  // keyが同じ間は同じ参照 (effectの依存をkeyに寄せる)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const normalized = useMemo(() => normalizeSwapOptions(options), [key]);
  const currentRef = useRef({ scenarioId, options: normalized });
  currentRef.current = { scenarioId, options: normalized };

  const [confirmedPreview, setConfirmedPreview] = useState<NarrationDialogueSwapConfirmedPreview | null>(null);
  const [inFlight, setInFlight] = useState(false);
  const [debouncePending, setDebouncePending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const seqRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = () => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const issue = useCallback((reqScenarioId: string, reqOptions: NarrationDialogueSwapOptions) => {
    const seq = ++seqRef.current;
    setInFlight(true);
    setError(null);
    swapNarrationDialogue(reqScenarioId, { ...reqOptions, dry_run: true })
      .then((response) => {
        if (seq !== seqRef.current) return;
        setInFlight(false);
        const current = currentRef.current;
        if (current.scenarioId !== reqScenarioId || !optionsEqual(reqOptions, current.options)) return;
        setConfirmedPreview({ options: reqOptions, response });
      })
      .catch((err: unknown) => {
        if (seq !== seqRef.current) return;
        setInFlight(false);
        setError(err);
      });
  }, []);

  useEffect(() => {
    // 条件・開閉が変わったら、それまでの要求と確認済みプレビューをすべて無効にする
    seqRef.current += 1;
    clearTimer();
    setConfirmedPreview(null);
    setInFlight(false);
    setError(null);
    if (!open || !scenarioId || !isSwapOptionsComplete(normalized)) {
      setDebouncePending(false);
      return undefined;
    }
    setDebouncePending(true);
    const reqOptions = normalized;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setDebouncePending(false);
      issue(scenarioId, reqOptions);
    }, NARRATION_DIALOGUE_SWAP_DEBOUNCE_MS);
    return clearTimer;
  }, [open, scenarioId, normalized, issue]);

  const refresh = useCallback(() => {
    const { scenarioId: sid, options: opts } = currentRef.current;
    clearTimer();
    setDebouncePending(false);
    setConfirmedPreview(null);
    if (!sid || !isSwapOptionsComplete(opts)) return;
    issue(sid, opts);
  }, [issue]);

  const complete = isSwapOptionsComplete(normalized);
  // 確認済みでも条件が今と違えば (effectが走る前の描画を含めて) 表示しない
  const preview = confirmedPreview && optionsEqual(confirmedPreview.options, normalized)
    ? confirmedPreview.response
    : null;

  let status: NarrationDialogueSwapPreviewStatus;
  if (!open || !scenarioId || !complete) status = 'idle';
  else if (debouncePending) status = 'pending';
  else if (inFlight) status = 'loading';
  else if (error) status = 'error';
  else if (preview) status = 'ready';
  else status = 'pending';

  const canExecute = !!confirmedPreview
    && !!preview
    && status === 'ready'
    && !debouncePending
    && !inFlight
    && preview.applicable
    && (!preview.requires_narration_speaker || !!confirmedPreview.options.narration_speaker);

  const executePayload = canExecute && confirmedPreview
    ? { options: confirmedPreview.options, confirmation: confirmedPreview.response.confirmation }
    : null;

  return { preview, status, error, canExecute, executePayload, refresh };
}
